/**
 * Consent management — CA privacy hardening (July 2026).
 *
 * GTM (the only tracker on this site — no CallRail/ClickCease/Meta/Bing) loads
 * ONLY after the visitor clicks Accept, or on a return visit when the consent
 * cookie is already set. Global Privacy Control is honored: GPC browsers get
 * no banner and no trackers.
 *
 * Netlify reCAPTCHA on the contact form is a security control (bot/spam
 * protection), not an ad/analytics tracker — it is intentionally NOT gated,
 * and is disclosed in the privacy policy.
 */

const CONSENT_COOKIE = 'gvdbuilders_consent';
const CONSENT_MAX_AGE = 60 * 60 * 24 * 365; // 12 months
const DISMISSED_KEY = 'gvdbuilders_consent_dismissed'; // sessionStorage — banner returns next visit
const GTM_ID = 'GTM-TN3CHBH2';

// Matches the removed gatsby-plugin-google-tagmanager options:
// includeInDevelopment: false, defaultDataLayer: { platform: 'gatsby' }
const INCLUDE_IN_DEVELOPMENT = false;

export const gpcEnabled = () =>
	typeof navigator !== 'undefined' &&
	navigator.globalPrivacyControl === true;

export const hasConsent = () => {
	if (typeof document === 'undefined') return false;
	return document.cookie
		.split('; ')
		.some(c => c === `${CONSENT_COOKIE}=granted`);
};

export const wasDismissedThisSession = () => {
	try {
		return window.sessionStorage.getItem(DISMISSED_KEY) === 'true';
	} catch (e) {
		return false;
	}
};

// Dismiss (X) = session-only flag. No trackers ever load without Accept.
export const dismissForSession = () => {
	try {
		window.sessionStorage.setItem(DISMISSED_KEY, 'true');
	} catch (e) {
		// ignore — banner will just reappear
	}
};

let trackersLoaded = false;

/**
 * Inject GTM. Safe to call multiple times (no-ops after first load).
 * Only call after consent has been granted.
 */
export const loadTrackers = () => {
	if (trackersLoaded) return;
	if (typeof window === 'undefined') return;
	if (gpcEnabled()) return;
	if (!INCLUDE_IN_DEVELOPMENT && process.env.NODE_ENV !== 'production')
		return;

	trackersLoaded = true;

	window.dataLayer = window.dataLayer || [];
	function gtag() {
		window.dataLayer.push(arguments);
	}

	// Google Consent Mode v2: flip everything to granted BEFORE GTM loads
	gtag('consent', 'update', {
		ad_storage: 'granted',
		analytics_storage: 'granted',
		ad_user_data: 'granted',
		ad_personalization: 'granted'
	});

	// Replicate the old plugin's defaultDataLayer
	window.dataLayer.push({ platform: 'gatsby' });

	// Standard GTM snippet, injected dynamically
	window.dataLayer.push({
		'gtm.start': new Date().getTime(),
		event: 'gtm.js'
	});
	const script = document.createElement('script');
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
	document.head.appendChild(script);
};

/** Accept: set the 12-month first-party cookie, then load trackers. */
export const grantConsent = () => {
	if (typeof document === 'undefined') return;
	document.cookie = `${CONSENT_COOKIE}=granted; max-age=${CONSENT_MAX_AGE}; path=/; SameSite=Lax; Secure`;
	loadTrackers();
};
